import React from 'react';
import { Box, Typography, Fab, useTheme, Tooltip, useMediaQuery } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { Artist, Album } from '@/types';
import { PLACEHOLDER_ARTIST } from '@/services/mediaService';
import { useAudio } from '@/context/AudioProvider';

interface Props {
  artist: Artist;
  firstPlayableAlbum: Album | null;
  onPlayAlbum: (album: Album) => void;
}

const ArtistHero: React.FC<Props> = ({ artist, firstPlayableAlbum, onPlayAlbum }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const { state, togglePlay } = useAudio();
  const { isPlaying, currentTrack } = state;

  const banner = artist.banner_url || artist.banner_image || PLACEHOLDER_ARTIST;
  const logo = artist.artist_logo || '';
  const profileImage = artist.image_url;

  const isCurrentAlbumPlaying = isPlaying && currentTrack?.albumName === firstPlayableAlbum?.title;

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!firstPlayableAlbum) return;

    if (isCurrentAlbumPlaying) {
      togglePlay();
    } else {
      onPlayAlbum(firstPlayableAlbum);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: isMobile ? 220 : 300,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.95)), url(${banner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        mb: 4,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        p: isMobile ? 2 : 4,
      }}
    >
      {/* Artist Logo */}
      {logo ? (
        <Box
          sx={{
            maxWidth: isMobile ? '180px' : '250px',
            maxHeight: isMobile ? '40px' : '60px',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="logo"
            sx={{
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              objectPosition: 'left',
            }}
          />
        </Box>
      ) : (
        <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight="bold" zIndex={2}>
          {artist.name}
        </Typography>
      )}

      {/* Play Button */}
      <Tooltip title={!firstPlayableAlbum ? "No playable tracks available" : ""}>
        <span>
          <Fab
            color="primary"
            aria-label="play"
            onClick={handlePlay}
            disabled={!firstPlayableAlbum}
            sx={{
              mt: 2,
              bgcolor: '#FF0054',
              '&:hover': { bgcolor: firstPlayableAlbum ? '#D70046' : undefined },
              '&.Mui-disabled': { bgcolor: 'rgba(255, 0, 84, 0.3)' },
              width: isMobile ? 44 : 56,
              height: isMobile ? 44 : 56,
              zIndex: 900,
            }}
          >
            {isCurrentAlbumPlaying ? <PauseIcon fontSize="medium" /> : <PlayArrowIcon fontSize="medium" />}
          </Fab>
        </span>
      </Tooltip>

      {/* Profile Image */}
      {profileImage && (
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? 16 : 30,
            right: isMobile ? 16 : 30,
            width: isMobile ? 100 : 180,
            height: isMobile ? 100 : 180,
            borderRadius: '60%',
            overflow: 'hidden',
            border: '0.5px solid black',
            zIndex: 1,
          }}
        >
          <Box
            component="img"
            src={profileImage}
            alt={artist.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ArtistHero;

